/**
 * 検査結果表示ページ
 *
 * 線分二等分課題の検査結果を表示
 * サマリー、試行詳細、偏差グラフを含む
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../services/api';
import { downloadCsv } from '../services/api';
import { offlineStorage } from '../services/offlineStorage';
import { syncPendingExams } from '../services/syncService';
import { Patient, ExamSettings, TrialData, ExamResultSummary } from '../types';

interface MonitorInfo {
  width?: number;
  height?: number;
  availWidth?: number;
  availHeight?: number;
  colorDepth?: number;
  pixelDepth?: number;
  devicePixelRatio: number;
  isPrimary?: boolean;
  label?: string;
  isInternal?: boolean;
  left?: number;
  top?: number;
  availLeft?: number;
  availTop?: number;
}

interface DeviceInfo {
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  devicePixelRatio: number;
  viewportWidth: number;
  viewportHeight: number;
  platform: string;
  monitors?: MonitorInfo[];
  monitorCount?: number;
}

function formatJstTimestampForFilename(date: Date): string {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(" ", "_")
    .replace(/:/g, "-");
}

function sanitizeForFilename(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, "-");
}

/**
 * sessionStorageから取得する検査結果の型
 */
interface ExamResultData {
  patient: Patient;
  settings: ExamSettings;
  trialResults: TrialData[];
  summary: ExamResultSummary;
}

/**
 * API保存用のリクエスト型
 */
interface SaveExamRequest {
  patientId: string;
  localId: string;
  settings: ExamSettings;
  trials: {
    trialNumber: number;
    lineLengthMm: number;
    linePositionXMm: number;
    linePositionYMm: number;
    markedPositionXMm: number;
    markedPositionYMm: number;
    deviationMm: number;
    deviationPercent: number;
    isAbnormal: boolean;
    capturedImage?: string;
  }[];
  createdAt: string;
  deviceInfo?: {
   userAgent: string;
   screenWidth: number;
   screenHeight: number;
   devicePixelRatio: number;
   viewportWidth: number;
   viewportHeight: number;
   platform: string;
 };
}

/**
 * API保存のレスポンス型
 */
interface SaveExamResponse {
  id: string;
  localId: string;
  patientId: string;
  examinerId: string;
}

/**
 * ローカルID生成
 */
function generateLocalId(): string {
  return `exam-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

type ScreenLike = {
  label?: string;
  isPrimary?: boolean;
  isInternal?: boolean;
  width?: number;
  height?: number;
  availWidth?: number;
  availHeight?: number;
  left?: number;
  top?: number;
  availLeft?: number;
  availTop?: number;
  devicePixelRatio?: number;
  colorDepth?: number;
  pixelDepth?: number;
};

type ScreenDetailsLike = {
  screens?: ScreenLike[];
};

type WindowWithScreenDetails = Window & {
  getScreenDetails?: () => Promise<ScreenDetailsLike>;
};

function toMonitorInfo(
  screenLike: ScreenLike,
  isPrimaryFallback = false,
): MonitorInfo {
  return {
    label: screenLike.label,
    isPrimary: screenLike.isPrimary ?? isPrimaryFallback,
    isInternal: screenLike.isInternal,
    width: screenLike.width,
    height: screenLike.height,
    availWidth: screenLike.availWidth,
    availHeight: screenLike.availHeight,
    left: screenLike.left,
    top: screenLike.top,
    availLeft: screenLike.availLeft,
    availTop: screenLike.availTop,
    devicePixelRatio: screenLike.devicePixelRatio ?? window.devicePixelRatio,
    colorDepth: screenLike.colorDepth,
    pixelDepth: screenLike.pixelDepth,
  };
}

async function collectDeviceInfo(): Promise<DeviceInfo> {
  const base: DeviceInfo = {
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    devicePixelRatio: window.devicePixelRatio,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    platform: navigator.platform,
  };

  const fallbackMonitors = [
    toMonitorInfo(window.screen as unknown as ScreenLike, true),
  ];
  const withFallback: DeviceInfo = {
    ...base,
    monitorCount: fallbackMonitors.length,
    monitors: fallbackMonitors,
  };

  const getScreenDetails = (window as WindowWithScreenDetails).getScreenDetails;
  if (typeof getScreenDetails !== "function") {
    return withFallback;
  }

  try {
    const details = await getScreenDetails();
    const screens = Array.isArray(details?.screens) ? details.screens : [];
    if (screens.length === 0) {
      return withFallback;
    }
    const monitors = screens.map((screen, index) =>
      toMonitorInfo(screen, index === 0),
    );
    return {
      ...base,
      monitorCount: monitors.length,
      monitors,
    };
  } catch {
    return withFallback;
  }
}

/**
 * 偏差バーグラフコンポーネント
 * 中央が0で、左が負（左偏差）、右が正（右偏差）を表現
 */
function DeviationGraph({
  trialResults,
  threshold,
}: {
  trialResults: TrialData[];
  threshold: number;
}) {
  // 偏差の最大絶対値を計算してスケール決定
  const maxAbsDeviation = Math.max(
    ...trialResults.map((t) => Math.abs(t.deviationMm)),
    threshold,
  );
  const scale = 40 / maxAbsDeviation; // 40%を最大幅として使用

  return (
    <div className="space-y-1">
      {/* ヘッダー */}
      <div className="flex items-center text-xs text-gray-500 mb-2">
        <div className="w-12 text-right mr-2">試行</div>
        <div className="flex-1 flex justify-between">
          <span>左偏差</span>
          <span>0</span>
          <span>右偏差</span>
        </div>
      </div>

      {/* 各試行のバー */}
      {trialResults.map((trial, index) => {
        const barWidth = Math.abs(trial.deviationMm) * scale;
        const isLeft = trial.deviationMm < 0;

        return (
          <div key={`${trial.trialNumber}-${trial.lineLengthMm}-${index}`} className="flex items-center h-6">
            {/* 試行番号 */}
            <div className="w-12 text-right text-xs text-gray-600 mr-2">
              {trial.trialNumber}
            </div>

            {/* バーエリア */}
            <div className="flex-1 flex items-center">
              {/* 左側（負の偏差） */}
              <div className="w-1/2 flex justify-end pr-px">
                {isLeft && (
                  <div
                    className={`h-4 rounded-l ${
                      trial.isAbnormal ? "bg-red-500" : "bg-blue-500"
                    }`}
                    style={{ width: `${barWidth}%` }}
                  />
                )}
              </div>

              {/* 中央線 */}
              <div className="w-px bg-gray-300 h-5" />

              {/* 右側（正の偏差） */}
              <div className="w-1/2 flex justify-start pl-px">
                {!isLeft && trial.deviationMm !== 0 && (
                  <div
                    className={`h-4 rounded-r ${
                      trial.isAbnormal ? "bg-red-500" : "bg-blue-500"
                    }`}
                    style={{ width: `${barWidth}%` }}
                  />
                )}
              </div>
            </div>

            {/* 偏差値表示 */}
            <div className="w-20 text-right text-xs text-gray-600 ml-2">
              {trial.deviationMm.toFixed(1)}mm
            </div>
          </div>
        );
      })}

      {/* 閾値ライン（凡例） */}
      <div className="flex items-center justify-center mt-4 text-xs text-gray-500 space-x-4">
        <div className="flex items-center">
          <div className="w-4 h-3 bg-blue-500 rounded mr-1" />
          <span>正常範囲</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-3 bg-red-500 rounded mr-1" />
          <span>異常（閾値: {threshold}%超）</span>
        </div>
      </div>
    </div>
  );
}

/**
 * 試行画像拡大モーダル
 */
function ImageModal({
  imageData,
  trialNumber,
  patientCode,
  capturedAt,
  onClose,
}: {
  imageData: string;
  trialNumber: number;
  patientCode: string;
  capturedAt: string;
  onClose: () => void;
}) {
  const handleDownload = () => {
    const safePatientCode = sanitizeForFilename(patientCode);
    const timestampStr = formatJstTimestampForFilename(new Date(capturedAt));
    const a = document.createElement("a");
    a.href = imageData;
    a.download = `trial_${trialNumber}_${safePatientCode}_${timestampStr}.png`;
    a.click();
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[200] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800">
            試行 {trialNumber} - キャプチャ画像
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="p-4 overflow-auto">
          <img
            src={imageData}
            alt={`試行 ${trialNumber} のキャプチャ`}
            className="block mx-auto max-w-full max-h-[70vh] object-contain border border-gray-200 rounded bg-white"
          />
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
          <button
            onClick={handleDownload}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            画像をダウンロード
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 検査結果ページコンポーネント
 */
export default function ExamResultPage() {
  const [deviceInfo, setDeviceInfo] = useState<any>(null);
  const navigate = useNavigate();
  const [resultData, setResultData] = useState<ExamResultData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savedOffline, setSavedOffline] = useState(false);
  const [savedExamId, setSavedExamId] = useState<string | null>(null);
  const [localId] = useState<string>(() => generateLocalId());
  const [createdAt] = useState<string>(() => new Date().toISOString());
  const [selectedImage, setSelectedImage] = useState<{
    data: string;
    trialNumber: number;
    patientCode: string;
    capturedAt: string;
  } | null>(null);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    if (selectedImage) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [selectedImage]);

  /**
   * sessionStorageから結果を取得
   */
  useEffect(() => {
    const stored = sessionStorage.getItem("examResult");
    if (stored) {
      try {
        const data = JSON.parse(stored) as ExamResultData;
        setResultData(data);
      } catch {
        console.error("検査結果の読み込みに失敗しました");
        navigate("/exam/settings");
      }
    } else {
      navigate("/exam/settings");
    }
  }, [navigate]);

  useEffect(() => {
    collectDeviceInfo()
      .then((info) => setDeviceInfo(info))
      .catch(() => {
        // Ignore monitor API failures and keep basic screen information only.
      });
  }, []);

  /**
   * 結果を保存（API呼び出し）
   */
  const handleSave = useCallback(async () => {
    if (!resultData) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    const capturedDeviceInfo = deviceInfo ?? (await collectDeviceInfo());
    const requestBody: SaveExamRequest = {
      patientId: resultData.patient.id,
      localId,
      settings: resultData.settings,
      trials: resultData.trialResults.map((trial) => ({
        trialNumber: trial.trialNumber,
        lineLengthMm: trial.lineLengthMm,
        linePositionXMm: trial.linePositionXMm,
        linePositionYMm: trial.linePositionYMm,
        markedPositionXMm: trial.markedPositionXMm,
        markedPositionYMm: trial.markedPositionYMm,
        deviationMm: trial.deviationMm,
        deviationPercent: trial.deviationPercent,
        isAbnormal: trial.isAbnormal,
        capturedImage: trial.capturedImage,
      })),
      createdAt,
      deviceInfo: capturedDeviceInfo,
    };

    try {
      const response = await api.post<SaveExamResponse>(
        "/api/exams",
        requestBody,
      );
      setSaveSuccess(true);
      setSavedOffline(false);
      setSavedExamId(response.id);
    } catch (error: unknown) {
      const saveOffline = async (sourceError?: Error) => {
        try {
          await offlineStorage.save(requestBody);
          setSaveSuccess(true);
          setSavedOffline(true);
          syncPendingExams().catch(() => {
            // 同期失敗は無視（次回の起動/オンライン復帰時にリトライ）
          });
        } catch (offlineError) {
          console.error("オフライン保存にも失敗:", offlineError);
          setSaveError(
            sourceError?.message || "保存に失敗しました。もう一度お試しください。",
          );
        }
      };

      if (error instanceof ApiError) {
        // Conflict（既に保存済み）の場合
        if (error.status === 409) {
          setSaveSuccess(true);
          setSavedOffline(false);
          setSaveError("この検査は既に保存されています");
        } else if (error.status >= 500) {
          await saveOffline(error);
        } else {
          setSaveError(error.message || "保存に失敗しました。入力内容を確認してください。");
        }
      } else {
        // ネットワークエラー（TypeError: Failed to fetch等）: オフライン保存
        await saveOffline(error instanceof Error ? error : undefined);
      }
      console.error("Save error:", error);
    } finally {
      setIsSaving(false);
    }
  }, [resultData, localId, createdAt, deviceInfo]);

  /**
   * 新しい検査を開始
   */
  const handleNewExam = () => {
    sessionStorage.removeItem("examResult");
    sessionStorage.removeItem("examPrepareState");
    navigate("/exam/settings");
  };

  /**
   * 患者詳細へ遷移
   */
  const handleGoToPatient = () => {
    if (resultData?.patient.id) {
      navigate(`/patients/${resultData.patient.id}`);
    }
  };

  /**
   * ホームへ遷移
   */
  const handleGoHome = () => {
    navigate("/home");
  };

  // ローディング中
  if (!resultData) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-600">結果を読み込み中...</div>
      </div>
    );
  }

  const { patient, settings, trialResults, summary } = resultData;

  // 検査日時を取得（最初の試行のタイムスタンプ）
  const examDate =
    trialResults.length > 0 ? new Date(trialResults[0].timestamp) : new Date();
  const displayDeviceInfo: DeviceInfo = deviceInfo ?? {
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    devicePixelRatio: window.devicePixelRatio,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    platform: navigator.platform,
    monitorCount: 1,
    monitors: [toMonitorInfo(window.screen as unknown as ScreenLike, true)],
  };

  return (
    <div className="max-w-4xl mx-auto print:max-w-none">
      {/* ページタイトル */}
      <div className="flex items-center justify-between mb-6 print:mb-4">
        <h1 className="text-2xl font-bold text-gray-800">検査結果</h1>
        <div className="print:hidden space-x-2">
          {savedExamId && (
            <button
              onClick={() => {
                const timestampStr = formatJstTimestampForFilename(examDate);
                downloadCsv(
                  savedExamId,
                  `exam_${patient.patientCode}_${timestampStr}.csv`,
                );
              }}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
            >
              CSV
            </button>
          )}
          <button
            onClick={() => window.print()}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
          >
            印刷
          </button>
        </div>
      </div>

      {/* 患者情報・検査日時カード */}
      <div className="bg-white rounded-lg shadow mb-6 print:shadow-none print:border">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">患者情報</h2>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500">患者コード</div>
              <div className="text-lg font-medium text-gray-800">
                {patient.patientCode}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">氏名</div>
              <div className="text-lg font-medium text-gray-800">
                {patient.name}
              </div>
            </div>
            <div className="col-span-2">
              <div className="text-sm text-gray-500">検査日時</div>
              <div className="text-lg font-medium text-gray-800">
                {examDate.toLocaleString("ja-JP", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* サマリーカード */}
      <div className="bg-white rounded-lg shadow mb-6 print:shadow-none print:border">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">検査サマリー</h2>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-3xl font-bold text-gray-800">
                {summary.totalTrials}
              </div>
              <div className="text-sm text-gray-500 mt-1">総試行数</div>
            </div>
            <div
              className={`text-center p-4 rounded-lg ${
                summary.abnormalTrials > 0 ? "bg-red-50" : "bg-gray-50"
              }`}
            >
              <div
                className={`text-3xl font-bold ${
                  summary.abnormalTrials > 0 ? "text-red-600" : "text-gray-800"
                }`}
              >
                {summary.abnormalTrials}
              </div>
              <div className="text-sm text-gray-500 mt-1">異常試行数</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-3xl font-bold text-gray-800">
                {summary.averageDeviationMm.toFixed(1)}
                <span className="text-lg font-normal text-gray-500">mm</span>
              </div>
              <div className="text-sm text-gray-500 mt-1">平均偏差</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-3xl font-bold text-gray-800">
                {summary.averageDeviationPercent.toFixed(1)}
                <span className="text-lg font-normal text-gray-500">%</span>
              </div>
              <div className="text-sm text-gray-500 mt-1">平均偏差率</div>
            </div>
          </div>

          {/* 追加の統計情報 */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-semibold text-gray-700">
                  {summary.minDeviationMm.toFixed(1)}mm
                </div>
                <div className="text-xs text-gray-500">最小偏差</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-700">
                  {summary.maxDeviationMm.toFixed(1)}mm
                </div>
                <div className="text-xs text-gray-500">最大偏差</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-700">
                  {summary.stdDeviationMm.toFixed(2)}mm
                </div>
                <div className="text-xs text-gray-500">標準偏差</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 端末情報（折りたたみ） */}
      <details className="bg-white rounded-lg shadow mb-6 print:shadow-none print:border">
        <summary className="px-6 py-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors">
          <h2 className="text-lg font-semibold text-gray-800 inline">
            端末情報
          </h2>
          <span className="ml-2 text-sm text-gray-500">（クリックで展開）</span>
        </summary>
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">画面解像度:</span>
              <span className="ml-2 font-medium">
                {displayDeviceInfo.screenWidth}x{displayDeviceInfo.screenHeight}
              </span>
            </div>
            <div>
              <span className="text-gray-500">ビューポート:</span>
              <span className="ml-2 font-medium">
                {displayDeviceInfo.viewportWidth}x
                {displayDeviceInfo.viewportHeight}
              </span>
            </div>
            <div>
              <span className="text-gray-500">デバイスピクセル比:</span>
              <span className="ml-2 font-medium">
                {displayDeviceInfo.devicePixelRatio}
              </span>
            </div>
            <div>
              <span className="text-gray-500">プラットフォーム:</span>
              <span className="ml-2 font-medium">
                {displayDeviceInfo.platform}
              </span>
            </div>
            <div>
              <span className="text-gray-500">モニター数:</span>
              <span className="ml-2 font-medium">
                {displayDeviceInfo.monitorCount ??
                  displayDeviceInfo.monitors?.length ??
                  "-"}
              </span>
            </div>
            <div>
              <span className="text-gray-500">メインモニター:</span>
              <span className="ml-2 font-medium">
                {(() => {
                  const primary =
                    displayDeviceInfo.monitors?.find((m) => m.isPrimary) ??
                    displayDeviceInfo.monitors?.[0];
                  return primary
                    ? `${primary.width ?? "-"}x${primary.height ?? "-"}`
                    : "-";
                })()}
              </span>
            </div>
            <div className="col-span-2">
              <span className="text-gray-500">使用検索エンジン:</span>
              <span className="ml-2 font-medium text-xs break-all">
                {displayDeviceInfo.userAgent}
              </span>
            </div>
            {!!displayDeviceInfo.monitors?.length && (
              <div className="col-span-2">
                <span className="text-gray-500">モニター:</span>
                <div className="mt-1 space-y-1">
                  {displayDeviceInfo.monitors.map((monitor, index) => (
                    <div
                      key={`${monitor.label || "monitor"}-${index}`}
                      className="text-xs text-gray-700"
                    >
                      [{index + 1}] {monitor.label || "-"} /{" "}
                      {monitor.width ?? "-"}x{monitor.height ?? "-"}
                      {monitor.isPrimary ? " / primary" : ""}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </details>

      {/* 偏差グラフカード（折りたたみ） */}
      <details className="bg-white rounded-lg shadow mb-6 print:shadow-none print:border">
        <summary className="px-6 py-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors">
          <h2 className="text-lg font-semibold text-gray-800 inline">
            偏差分布
          </h2>
          <span className="ml-2 text-sm text-gray-500">（クリックで展開）</span>
        </summary>
        <div className="px-6 py-4">
          <DeviationGraph
            trialResults={trialResults}
            threshold={settings.abnormalThreshold}
          />
        </div>
      </details>

      {/* 試行詳細カード（折りたたみ） */}
      <details className="bg-white rounded-lg shadow mb-6 print:shadow-none print:border">
        <summary className="px-6 py-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors">
          <h2 className="text-lg font-semibold text-gray-800 inline">
            試行詳細
          </h2>
          <span className="ml-2 text-sm text-gray-500">（クリックで展開）</span>
        </summary>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  試行
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  線の長さ
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  偏差 (mm)
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  偏差 (%)
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  判定
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider print:hidden">
                  画像
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {trialResults.map((trial, index) => (
                <tr
                  key={`${trial.trialNumber}-${trial.lineLengthMm}-${index}`}
                  className={trial.isAbnormal ? "bg-red-50" : ""}
                >
                  <td className="px-4 py-3 text-sm text-gray-800">
                    {trial.trialNumber}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800 text-right">
                    {trial.lineLengthMm}mm
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800 text-right">
                    {trial.deviationMm.toFixed(2)}mm
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800 text-right">
                    {trial.deviationPercent.toFixed(2)}%
                  </td>
                  <td className="px-4 py-3 text-center">
                    {settings.disableAbnormalJudgment ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                        判定無し
                      </span>
                    ) : trial.isAbnormal ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        異常
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        正常
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center print:hidden">
                    {trial.capturedImage ? (
                      <button
                        onClick={() =>
                          setSelectedImage({
                            data: trial.capturedImage!,
                            trialNumber: trial.trialNumber,
                            patientCode: patient.patientCode,
                            capturedAt: trial.timestamp,
                          })
                        }
                        className="inline-block border border-gray-200 rounded hover:border-blue-400 transition-colors"
                        title="クリックで拡大"
                      >
                        <img
                          src={trial.capturedImage}
                          alt={`試行 ${trial.trialNumber}`}
                          className="w-16 h-10 object-contain"
                        />
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {/* アクションボタン（印刷時は非表示） */}
      <div className="print:hidden bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* 結果を保存 */}
          <button
            onClick={handleSave}
            disabled={isSaving || saveSuccess}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
              saveSuccess
                ? "bg-green-100 text-green-700 cursor-default"
                : isSaving
                  ? "bg-gray-300 text-gray-500 cursor-wait"
                  : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {saveSuccess ? (
              <span className="flex items-center justify-center">
                <svg
                  className="w-5 h-5 mr-2 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                保存済み
              </span>
            ) : isSaving ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                保存中...
              </span>
            ) : (
              "結果を保存"
            )}
          </button>

          {/* 新しい検査 */}
          <button
            onClick={handleNewExam}
            className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            新しい検査
          </button>

          {/* 患者詳細へ */}
          <button
            onClick={handleGoToPatient}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            患者詳細へ
          </button>

          {/* ホームへ */}
          <button
            onClick={handleGoHome}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            ホームへ
          </button>
        </div>

        {/* 保存成功メッセージ（サーバー保存） */}
        {saveSuccess && savedExamId && !savedOffline && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span>検査結果を保存しました</span>
            </div>
            <p className="mt-1 text-xs text-green-600">検査ID: {savedExamId}</p>
          </div>
        )}

        {/* オフライン保存メッセージ */}
        {saveSuccess && savedOffline && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2 text-amber-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span>
                オフライン保存しました。オンライン復帰時に自動同期されます
              </span>
            </div>
          </div>
        )}

        {/* エラーメッセージ */}
        {saveError && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
            <p className="text-red-600 text-sm">{saveError}</p>
          </div>
        )}
      </div>

      {/* 印刷用フッター */}
      <div className="hidden print:block mt-8 pt-4 border-t border-gray-200 text-xs text-gray-500 text-center">
        線分二等分課題検査結果 - 印刷日時: {new Date().toLocaleString("ja-JP")}
      </div>

      {/* 画像拡大モーダル */}
      {selectedImage && (
        <ImageModal
          imageData={selectedImage.data}
          trialNumber={selectedImage.trialNumber}
          patientCode={selectedImage.patientCode}
          capturedAt={selectedImage.capturedAt}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
}
